/**
 * WorldMapHero — illustrated world-map background with floating
 * photo cards linked by connection lines. Matches the 2026-02-18
 * mockup for socialpulse.com hero art.
 *
 * Uses public Unsplash / Pexels community photos as the floating
 * cards; each card connects to a colored pin on the map, and the
 * map itself is an inline SVG so it stays crisp at every viewport.
 */

const CARDS = [
  {
    key: "people",
    img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=200&q=70",
    title: "People", sub: "Build Brighter Communities",
    left: "8%", top: "18%",
    pin: { x: "22%", y: "34%", color: "#7B4A9C" },
  },
  {
    key: "stronger",
    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=200&q=70",
    title: "Stronger Together", sub: "Everywhere",
    right: "18%", top: "8%",
    pin: { x: "78%", y: "20%", color: "#1E64C8" },
  },
  {
    key: "local",
    img: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=200&q=70",
    title: "Local Solutions", sub: "Real Change",
    right: "2%", top: "28%",
    pin: { x: "88%", y: "40%", color: "#F58220" },
  },
  {
    key: "communities",
    img: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=200&q=70",
    title: "Communities", sub: "Belong Here",
    left: "10%", top: "48%",
    pin: { x: "28%", y: "58%", color: "#059669" },
  },
  {
    key: "explore",
    img: "https://images.unsplash.com/photo-1500835556837-99ac94a94552?auto=format&fit=crop&w=200&q=70",
    title: "Explore · Share", sub: "Make a Difference",
    left: "42%", top: "68%",
    pin: { x: "50%", y: "78%", color: "#1E64C8" },
  },
  {
    key: "kind",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=70",
    title: "Kind People", sub: "Stronger Communities",
    right: "6%", top: "50%",
    pin: { x: "82%", y: "60%", color: "#F58220" },
  },
  {
    key: "perspectives",
    img: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=200&q=70",
    title: "New Perspectives", sub: "A More Connected World",
    right: "20%", top: "70%",
    pin: { x: "72%", y: "82%", color: "#7B4A9C" },
  },
];

export default function WorldMapHero() {
  return (
    <div
      className="relative w-full aspect-[7/5] rounded-3xl overflow-hidden"
      style={{ background: "linear-gradient(135deg, #F5FBFF 0%, #EAF3FF 100%)" }}
      data-testid="world-map-hero"
      aria-hidden="true"
    >
      {/* SVG world-map background */}
      <svg
        viewBox="0 0 800 560"
        className="absolute inset-0 w-full h-full opacity-70"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="wm-glow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#93C5FD" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#93C5FD" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="400" cy="280" r="360" fill="url(#wm-glow)" />
        {/* Continents: rough dotted stipple pattern using circles */}
        {stipplePattern.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#3B82F6" opacity="0.55" />
        ))}
        {/* Connection lines between pins */}
        <g stroke="#1E64C8" strokeWidth="1.2" strokeDasharray="3 4" fill="none" opacity="0.5">
          <path d="M 176 190  Q 300 60 624 112" />
          <path d="M 176 190  Q 260 300 400 436" />
          <path d="M 624 112  Q 720 260 704 336" />
          <path d="M 224 324  Q 350 400 400 436" />
          <path d="M 400 436  Q 580 400 576 460" />
        </g>
        {/* Pin markers */}
        {CARDS.map((c) => (
          <g key={`pin-${c.key}`}>
            <circle cx={pinX(c)} cy={pinY(c)} r="10" fill={c.pin.color} opacity="0.2">
              <animate attributeName="r" from="6" to="16" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" from="0.4" to="0" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx={pinX(c)} cy={pinY(c)} r="5" fill={c.pin.color} />
          </g>
        ))}
      </svg>

      {/* Center tagline */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none"
        data-testid="world-map-tagline"
      >
        <div className="font-serif italic text-[#0B1226] text-lg sm:text-xl leading-tight">
          Different <span className="not-italic font-medium">Places</span>
        </div>
        <div className="font-serif italic text-[#0B1226] text-lg sm:text-xl leading-tight">
          Brighter <span className="not-italic font-medium">People</span>
        </div>
        <div className="font-serif italic text-[#1E64C8] text-lg sm:text-xl leading-tight border-b-2 border-[#1E64C8] pb-0.5 inline-block">
          A Stronger Tomorrow
        </div>
      </div>

      {/* Floating photo cards */}
      {CARDS.map((c) => (
        <div
          key={c.key}
          className="absolute bg-white rounded-2xl shadow-lg overflow-hidden flex items-center gap-2 pr-3 max-w-[210px] hover:scale-[1.03] transition-transform duration-300"
          style={{
            left: c.left,  right: c.right,
            top: c.top,
            transform: c.key === "explore" ? "translateX(-50%)" : undefined,
          }}
          data-testid={`world-map-card-${c.key}`}
        >
          <img
            src={c.img}
            alt=""
            className="w-14 h-14 object-cover shrink-0"
            loading="lazy"
          />
          <div className="min-w-0 py-1">
            <div className="text-[11px] font-semibold text-[#0B1226] leading-tight truncate">
              {c.title}
            </div>
            <div className="text-[10px] text-[#50586C] leading-tight truncate">
              {c.sub}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function pinX(c) { return parseFloat(c.pin.x) * 8; }  // 800 * pct/100
function pinY(c) { return parseFloat(c.pin.y) * 5.6; } // 560 * pct/100

// Coarse continent-shaped stipple (Americas, Europe/Africa, Asia, Oceania)
const stipplePattern = (() => {
  const pts = [];
  // Americas (left cluster)
  for (let i = 0; i < 90; i++) {
    const x = 110 + Math.random() * 130;
    const y = 120 + Math.random() * 320;
    if (Math.random() > 0.15) pts.push([x, y]);
  }
  // Europe/Africa (center-left cluster)
  for (let i = 0; i < 110; i++) {
    const x = 320 + Math.random() * 150;
    const y = 90 + Math.random() * 380;
    if (Math.random() > 0.2) pts.push([x, y]);
  }
  // Asia (center-right cluster)
  for (let i = 0; i < 130; i++) {
    const x = 500 + Math.random() * 200;
    const y = 90 + Math.random() * 280;
    if (Math.random() > 0.15) pts.push([x, y]);
  }
  // Oceania (bottom-right cluster)
  for (let i = 0; i < 60; i++) {
    const x = 580 + Math.random() * 140;
    const y = 380 + Math.random() * 110;
    if (Math.random() > 0.2) pts.push([x, y]);
  }
  return pts;
})();
